import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants';
import { LeagueShowWrapper } from '@seedlang/hoc';
import { AppUI } from '@seedlang/state';
import { flexCenterColumn } from '@seedlang/style_mixins';
import { isBlank, isPresent } from '@seedlang/utils';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import Spinner from 'components/spinner';
import { observer } from 'mobx-react';
import React from 'react';
import LeagueBadgeIcon from './league_badge_icon';
import LeagueHistory from './league_history';
import LeagueTable from './league_table';
import withRouteParams from 'utils/with_route_params';

const Wrapper = styled.div`
  ${flexCenterColumn()}
  width: 100%;
  padding: 20px;
`;

const LeagueTitle = styled.h2`
  text-align: center;
`;

const Note = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${props => props.margin};
  font-size: 14px;
  font-color: #333;
  font-weight: bold;
  color: ${props => props.color || Theme.darkBlue};
`;

const HistoryTitle = styled.div`
  display: block;
  width: 100%;
  text-align: center;
  font-size: 20px;
  line-height: 20px;
  font-weight: bold;
  margin: 30px 0 20px 0;
`;

const LeagueBadges = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 0 20px 0;
`;

const NotesWrapper = styled.div`
  margin: 0 0 20px 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

@observer
class LeagueShow extends React.Component {

  componentDidMount() {
    const leagueId = this.props.params?.leagueId || this.props.league?.id;
    if (isPresent(leagueId)) {
      AppUI.createEvent('leagues - view page', { league_id: leagueId });
    }
  }

  badgeWidth(num) {
    if (AppUI.layout.isMobile) {
      if (num === this.props.league.leagueLevel?.position) {
        return '60px';
      }
      return '50px';
    }
    if (num === this.props.league.leagueLevel?.position) {
      return '95px';
    }
    return '70px';
  }

  render() {
    if (isBlank(this.props.league)) {
      return (
        <Wrapper>
          <Spinner />
        </Wrapper>
      );
    }
    return (
      <Wrapper>
        <LeagueBadges>
          {[1, 2, 3, 4, 5, 6].map(num => (
            <LeagueBadgeIcon
              key={num}
              leagueLevelPosition={num <= this.props.league.leagueLevel?.position ? num : 0}
              width={this.badgeWidth(num)}
              margin='0 5px'
            />
          ))}
        </LeagueBadges>
        <LeagueTitle>{this.props.league.leagueLevel?.name}</LeagueTitle>
        <NotesWrapper>
          {
            this.props.league.ended && !this.props.userIsInLeague &&
            <Note>
              This league has ended.
            </Note>
          }
          {
            this.props.user && this.props.league.ended && this.props.userIsInLeague &&
            <>
              <Note color='#000'>
                {
                  this.props.league.ended &&
                  `${this.props.userIsAuthUser ? 'You' : this.props.user?.name} finished #${this.props.userLeague?.rank},`
                }
                {
                  this.props.userLeague?.promoted ?
                    ` and ${this.props.userIsAuthUser ? 'were' : 'was'} promoted to the next league.`
                    : this.props.userLeague?.demoted ?
                      ` and ${this.props.userIsAuthUser ? 'were' : 'was'} demoted to the previous league.`
                      :
                      ` and will stay in the ${this.props.league.leagueLevel?.name}.`
                }
              </Note>
              {
                this.props.userIsAuthUser && this.props.isUsersLastLeague &&
                <Note>
                  Earn XP to join your new league.
                </Note>
              }
            </>
          }
          {
            !this.props.league.ended &&
            <>
              {
                this.props.user && this.props.userIsInLeague &&
                <Note color='#000'>
                  {this.props.statusText}
                </Note>
              }
              <Note>
                {this.props.leagueEndedText}
              </Note>
            </>
          }
          {
            this.props.isFirstLeagueMember &&
            <Note
              color={Theme.darkRed}
            >
              {this.props.firstLeagueMemberText}
            </Note>
          }
        </NotesWrapper>
        <LeagueTable
          league={this.props.league}
          userId={this.props.userId}
        />
        {
          isPresent(this.props.user) &&
          <>
            <HistoryTitle>{this.props.userIsAuthUser ? 'Your League History' : `${this.props.user?.name}'s League History`}</HistoryTitle>
            <LeagueHistory
              hideActiveLeague={this.props.league?.active}
              userId={this.props.user?.id}
              selectedLeague={this.props.league}
            />
          </>
        }
      </Wrapper>
    );
  }
}

export default withRouteParams(ErrorBoundary(LeagueShowWrapper(LeagueShow)));
